import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import type { ComputedRef } from 'vue'
import type { Props } from '../types'

import { NotificationType as NotificationTypeEnum } from '@/constants/enums/NotificationType'
import { formatUserLabel } from '@/utils/User'

type UseNotificationPanelListCard = (
  props: Props,
) => {
  title: ComputedRef<string>
  description: ComputedRef<string>
  isIconMessage: ComputedRef<boolean>
}

export const useNotificationPanelListCard: UseNotificationPanelListCard = (
  props: Props,
) => {
  const { t } = useI18n()

  const title = computed(() => {
    switch (props.notificationMeta.notificationType) {
      case NotificationTypeEnum.ANSWER_REJECTION_MAIL:
        return t('common.notification.send_back.title')
      case NotificationTypeEnum.RE_ANSWER_REQUEST_MAIL:
        return t('common.notification.withdraw.title')
      case NotificationTypeEnum.CSV_DOWNLOAD_SUCCESS_MAIL:
        return t('common.notification.download.csv_success_title')
      case NotificationTypeEnum.CSV_DOWNLOAD_FAILED_MAIL:
        return t('common.notification.download.csv_failed_title')
      case NotificationTypeEnum.PDF_DOWNLOAD_SUCCESS_MAIL:
        return t('common.notification.download.pdf_success_title')
      case NotificationTypeEnum.PDF_DOWNLOAD_FAILED_MAIL:
        return t('common.notification.download.pdf_failed_title')
      case NotificationTypeEnum.ATTACH_FILE_DOWNLOAD_SUCCESS_MAIL:
        return t('common.notification.download.attachment_file_success_title')
      case NotificationTypeEnum.ATTACH_FILE_DOWNLOAD_FAILED_MAIL:
        return t('common.notification.download.attachment_file_failed_title')
      case NotificationTypeEnum.IMPROVEMENT_PLAN_MAIL_FROM_BUYER:
        return t('common.notification.message.title')
      case NotificationTypeEnum.IMPROVEMENT_PLAN_MAIL_FROM_SUPPLIER:
        return t('common.notification.message.title')
      case NotificationTypeEnum.ESG_DATA_INVITATION_MAIL:
        return t('common.notification.data_share_invitation.title')
      case NotificationTypeEnum.ESG_DATA_INVITATION_COMPLETE_MAIL:
        return t('common.notification.data_share_complete.title')
      case NotificationTypeEnum.ASUENE_REMIND_MAIL:
        return t('common.notification.answer_remind.title')
      case NotificationTypeEnum.BUYER_REMIND_MAIL:
        return t('common.notification.answer_remind.title')
      default:
        throw new Error(`${((props.notificationMeta satisfies never) as any)?.notificationType} is unexpected value`)
    }
  })

  const description = computed(() => {
    switch (props.notificationMeta.notificationType) {
      case NotificationTypeEnum.ANSWER_REJECTION_MAIL:
        return [
          t('common.notification.send_back.description_1', { questionGroupName: props.notificationMeta.meta.questionGroupName }),
          t('common.notification.send_back.description_2'),
        ].join('\n')
      case NotificationTypeEnum.RE_ANSWER_REQUEST_MAIL:
        return [
          t('common.notification.withdraw.description_1', { company: props.notificationMeta.meta.companyName }),
          t('common.notification.withdraw.description_2'),
        ].join('\n')
      case NotificationTypeEnum.CSV_DOWNLOAD_SUCCESS_MAIL:
        return [
          t('common.notification.download.csv_description_1'),
          t('common.notification.download.common_description'),
        ].join('\n')
      case NotificationTypeEnum.CSV_DOWNLOAD_FAILED_MAIL:
        return [
          t('common.notification.download.failed_description_1'),
          t('common.notification.download.failed_description_2'),
        ].join('\n')
      case NotificationTypeEnum.PDF_DOWNLOAD_SUCCESS_MAIL:
        return [
          t('common.notification.download.pdf_description_1'),
          t('common.notification.download.common_description'),
        ].join('\n')
      case NotificationTypeEnum.PDF_DOWNLOAD_FAILED_MAIL:
        return [
          t('common.notification.download.failed_description_1'),
          t('common.notification.download.failed_description_2'),
        ].join('\n')
      case NotificationTypeEnum.ATTACH_FILE_DOWNLOAD_SUCCESS_MAIL:
        return [
          t('common.notification.download.attachment_file_description_1'),
          t('common.notification.download.common_description'),
        ].join('\n')
      case NotificationTypeEnum.ATTACH_FILE_DOWNLOAD_FAILED_MAIL:
        return [
          t('common.notification.download.failed_description_1'),
          t('common.notification.download.failed_description_2'),
        ].join('\n')
      case NotificationTypeEnum.IMPROVEMENT_PLAN_MAIL_FROM_BUYER:
        return [
          t('common.notification.message.description', { company: props.notificationMeta.meta.companyName }),
        ].join('\n')
      case NotificationTypeEnum.IMPROVEMENT_PLAN_MAIL_FROM_SUPPLIER:
        return [
          t('common.notification.message.description', { company: props.notificationMeta.meta.companyName }),
        ].join('\n')
      case NotificationTypeEnum.ESG_DATA_INVITATION_MAIL:
        return [
          t('common.notification.data_share_invitation.description', { name: formatUserLabel(props.notificationMeta.meta) }),
        ].join('\n')
      case NotificationTypeEnum.ESG_DATA_INVITATION_COMPLETE_MAIL:
        return [
          t('common.notification.data_share_complete.description', { name: formatUserLabel(props.notificationMeta.meta) }),
        ].join('\n')
      case NotificationTypeEnum.ASUENE_REMIND_MAIL:
        return [
          t('common.notification.answer_remind.description', { questionGroupName: props.notificationMeta.meta.questionGroupName }),
        ].join('\n')
      case NotificationTypeEnum.BUYER_REMIND_MAIL:
        return [
          t('common.notification.answer_remind.description', { questionGroupName: props.notificationMeta.meta.questionGroupName }),
        ].join('\n')
      default:
        throw new Error(`${((props.notificationMeta satisfies never) as any)?.notificationType} is unexpected value`)
    }
  })

  const isIconMessage = computed(() => {
    switch (props.notificationMeta.notificationType) {
      case NotificationTypeEnum.ANSWER_REJECTION_MAIL:
      case NotificationTypeEnum.ASUENE_REMIND_MAIL:
      case NotificationTypeEnum.BUYER_REMIND_MAIL:
      case NotificationTypeEnum.ESG_DATA_INVITATION_COMPLETE_MAIL:
      case NotificationTypeEnum.ESG_DATA_INVITATION_MAIL:
      case NotificationTypeEnum.IMPROVEMENT_PLAN_MAIL_FROM_BUYER:
      case NotificationTypeEnum.IMPROVEMENT_PLAN_MAIL_FROM_SUPPLIER:
      case NotificationTypeEnum.RE_ANSWER_REQUEST_MAIL:
        return true
      case NotificationTypeEnum.CSV_DOWNLOAD_FAILED_MAIL:
      case NotificationTypeEnum.CSV_DOWNLOAD_SUCCESS_MAIL:
      case NotificationTypeEnum.PDF_DOWNLOAD_SUCCESS_MAIL:
      case NotificationTypeEnum.PDF_DOWNLOAD_FAILED_MAIL:
      case NotificationTypeEnum.ATTACH_FILE_DOWNLOAD_SUCCESS_MAIL:
      case NotificationTypeEnum.ATTACH_FILE_DOWNLOAD_FAILED_MAIL:
        return false
      default:
        throw new Error(`${((props.notificationMeta satisfies never) as any)?.notificationType} is unexpected value`)
    }
  })

  return {
    title,
    description,
    isIconMessage,
  }
}
